import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Faq, FaqInsert } from '@/services/content'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Loader2 } from 'lucide-react'
import { RichTextEditor } from './RichTextEditor'

const formSchema = z.object({
  question: z.string().min(3, 'Question must be at least 3 characters'),
  answer: z.string().min(5, 'Answer must be at least 5 characters'),
  sort_order: z.coerce.number().int().default(0),
})

interface FaqDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialData?: Faq | null
  onSubmit: (data: FaqInsert) => Promise<void>
  isSubmitting: boolean
  locale: string
}

export const FaqDialog = ({
  open,
  onOpenChange,
  initialData,
  onSubmit,
  isSubmitting,
  locale,
}: FaqDialogProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      question: '',
      answer: '',
      sort_order: 0,
    },
  })

  useEffect(() => {
    if (initialData) {
      form.reset({
        question: initialData.question,
        answer: initialData.answer,
        sort_order: initialData.sort_order,
      })
    } else {
      form.reset({
        question: '',
        answer: '',
        sort_order: 0,
      })
    }
  }, [initialData, form, open])

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    // Pass the locale alongside form values
    await onSubmit({ ...values, locale })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Edit FAQ' : 'New FAQ'} ({locale.toUpperCase()})
          </DialogTitle>
          <DialogDescription>
            Add or edit frequently asked questions for the landing page.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="question"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Question</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. How does it work?" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sort_order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="answer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Answer</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      placeholder="The answer to the question..."
                      className="min-h-[150px]"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
